<header
  class="app-toolbar"
  [class.is-scrolled]="isScrolled()"
  [class.app-toolbar--public]="canShowPublicDesktopNav()"
>

  <div class="toolbar-left">
    <a class="toolbar-brand" [routerLink]="brandRoute()">
      <span class="brand-icon brand-icon--mark" aria-hidden="true">
        <span class="brand-mark"></span>
      </span>
      <span class="brand-text-group">
        <span class="brand-name">PeerLiv</span>
        <span class="brand-caption">You're not alone</span>
      </span>
    </a>

    <nav class="toolbar-nav" role="navigation">
      @if (canShowDesktopNav()) {
        <a
          class="nav-link"
          routerLink="/home"
          routerLinkActive="nav-link--active"
          [routerLinkActiveOptions]="{ exact: true }"
        >
          <lucide-icon name="home"></lucide-icon>
          <span>Home</span>
        </a>

        <a
          class="nav-link"
          routerLink="/categories"
          routerLinkActive="nav-link--active"
        >
          <lucide-icon name="layout-grid"></lucide-icon>
          <span>Communities</span>
        </a>

      } @else if (canShowPublicDesktopNav()) {
        <a
          class="nav-link"
          routerLink="/landing"
          fragment="communities"
        >
          <lucide-icon name="users"></lucide-icon>
          <span>Communities</span>
        </a>

        <a
          class="nav-link"
          routerLink="/landing"
          fragment="how-it-works"
        >
          <lucide-icon name="compass"></lucide-icon>
          <span>How it Works</span>
        </a>

        <a
          class="nav-link"
          routerLink="/landing"
          fragment="our-promise"
        >
          <lucide-icon name="shield"></lucide-icon>
          <span>Our Promise</span>
        </a>

        <a
          class="nav-link"
          routerLink="/landing"
          fragment="faq"
        >
          <lucide-icon name="help-circle"></lucide-icon>
          <span>FAQ</span>
        </a>
      }
    </nav>
  </div>

  <div class="toolbar-right">
    @if (canShowDesktopNav()) {
      <a class="toolbar-post" routerLink="/ask" routerLinkActive="toolbar-post--active">
        <lucide-icon name="plus"></lucide-icon>
        <span>New Post</span>
      </a>
    }

    @if (isAuthenticated()) {
      <span class="toolbar-divider"></span>

      <button
        mat-icon-button
        class="toolbar-action toolbar-action--search"
        [class.toolbar-action--active]="searchOpen()"
        (click)="openSearch()"
        aria-label="Search"
      >
        <lucide-icon name="search"></lucide-icon>
      </button>

      <button
        mat-icon-button
        class="toolbar-action toolbar-notifications toolbar-activity"
        [class.toolbar-action--active]="notificationsOpen()"
        [class.has-unread]="unreadCount() > 0"
        (click)="toggleNotifications()"
        [attr.aria-label]="'Activity' + (unreadCount() > 0 ? ' - ' + unreadCount() + ' unread' : '')"
        [attr.title]="'Activity' + (unreadCount() > 0 ? ' (' + unreadCount() + ' unread)' : '')"
      >
        <lucide-icon name="bell"></lucide-icon>
        @if (unreadCount() > 0) {
          <span class="notification-badge">{{ unreadBadgeLabel() }}</span>
        }
      </button>

      <span class="toolbar-divider"></span>

      <button
        mat-icon-button
        class="toolbar-avatar"
        [class.toolbar-avatar--open]="userMenuOpen()"
        (click)="toggleUserMenu($event)"
        [attr.aria-expanded]="userMenuOpen()"
        aria-label="Profile menu"
      >
        <div class="avatar-circle" aria-hidden="true">
          @if (userAvatar()) {
            <img [src]="userAvatar()" alt="" />
          } @else {
            <span class="avatar-initials">{{ getInitials() }}</span>
          }
          @if (isOnline()) {
            <span class="online-indicator"></span>
          }
        </div>
      </button>
    } @else {
      <button
        mat-button
        class="toolbar-login-btn"
        routerLink="/login"
      >
        Login
      </button>
    }
  </div>

  @if (userMenuOpen()) {
    <div class="user-menu-overlay" (click)="closeUserMenu()">
      <div class="user-menu-panel profile-dropdown--design" (click)="$event.stopPropagation()">
        <div class="dd-header">
          <div class="dd-avatar">
            @if (userAvatar()) {
              <img [src]="userAvatar()" [alt]="userName()" />
            } @else {
              <span>{{ getInitials() }}</span>
            }
          </div>
          <div>
            <div class="dd-user-name">{{ userName() }}</div>
            <div class="dd-user-handle">Joined {{ memberSince() }}</div>
            <div class="dd-impact">
              <span class="dd-impact-item"><strong>{{ postCount() }}</strong> posts</span>
              <span class="dd-impact-item"><strong>{{ replyCount() }}</strong> replies</span>
              <span class="dd-impact-item"><strong>{{ helpfulCount() }}</strong> helped</span>
            </div>
          </div>
        </div>

        <div class="dd-section">
          <a class="dd-item" routerLink="/me" (click)="closeUserMenu()">
            <lucide-icon name="user" [size]="20"></lucide-icon>
            My Profile
          </a>
          <button type="button" class="dd-item" (click)="navigateFromUserMenu('/me/posts', $event)">
            <lucide-icon name="file-text" [size]="20"></lucide-icon>
            My Journal
          </button>
          <a class="dd-item" routerLink="/me/saved" (click)="closeUserMenu()">
            <lucide-icon name="bookmark" [size]="20"></lucide-icon>
            Saved Posts
            <span class="dd-shortcut">{{ savedCount() }}</span>
          </a>
          <button type="button" class="dd-item" (click)="navigateFromUserMenu('/me?panel=notifications', $event)">
            <lucide-icon name="settings" [size]="20"></lucide-icon>
            Settings
          </button>
        </div>

        @if (isAdmin()) {
          <div class="dd-section">
            <div class="dd-label">Admin</div>
            <a class="dd-item" routerLink="/admin/moderation" (click)="closeUserMenu()">
              <lucide-icon name="shield" [size]="20"></lucide-icon>
              Admin Dashboard
            </a>
          </div>
        }

        <div class="dd-section">
          <div class="dd-label">Help &amp; Info</div>
          <a class="dd-item" routerLink="/community-guidelines" (click)="closeUserMenu()">
            <lucide-icon name="shield" [size]="20"></lucide-icon>
            Community Guidelines
          </a>
          <a class="dd-item" routerLink="/contact" (click)="closeUserMenu()">
            <lucide-icon name="help-circle" [size]="20"></lucide-icon>
            Help &amp; Contact
          </a>
        </div>

        <div class="dd-section">
          <button type="button" class="dd-item dd-item--danger" (click)="signOut()">
            <lucide-icon name="log-out" [size]="20"></lucide-icon>
            Log Out
          </button>
        </div>
      </div>
    </div>
  }
</header>

@if (searchOpen()) {
  <div class="search-overlay" (click)="closeSearch()">
    <div class="search-container" (click)="$event.stopPropagation()">
      <div class="search-header">
        <lucide-icon class="search-icon" name="search"></lucide-icon>
        <input
          type="text"
          class="search-input"
          placeholder="Search conditions, symptoms, discussions..."
          [ngModel]="searchQuery()"
          (ngModelChange)="searchQuery.set($event)"
          (keyup.enter)="performSearch()"
          (keyup.escape)="closeSearch()"
          #searchInput
        />
        <button
          mat-icon-button
          class="search-close"
          (click)="closeSearch()"
          aria-label="Close search"
        >
          <lucide-icon name="x"></lucide-icon>
        </button>
      </div>

      <div class="search-body">
        <app-search-dropdown
          [context]="currentSearchContext()"
          [currentCommunityId]="activeCommunityId()"
          [searchQuery]="searchQuery()"
          (suggestionSelected)="onSuggestionSelected($event)"
          (closed)="closeSearch()"
        ></app-search-dropdown>
      </div>
    </div>
  </div>
}

@if (notificationsOpen()) {
  <div class="notifications-overlay" (click)="closeNotifications()">
    <div class="notifications-panel" (click)="$event.stopPropagation()">
      <div class="notifications-header">
        <div class="notifications-title">
          <h3>Notifications</h3>
          @if (unreadCount() > 0) {
            <span class="notifications-count">{{ unreadCount() }} unread</span>
          }
        </div>
        <div class="notifications-actions">
          @if (unreadCount() > 0) {
            <button
              mat-button
              class="mark-read-btn"
              (click)="markAllAsRead()"
            >
              Mark all read
            </button>
          }
        </div>
      </div>

      <div class="notifications-list">
        @if (notificationsLoading()) {
          <div class="notifications-loading">Loading notifications...</div>
        } @else if (groupedNotifications().length > 0) {
          @for (group of groupedNotifications(); track group.label) {
            <div class="notification-group">
              <div class="group-label">{{ group.label }}</div>
              @for (notification of group.notifications; track notification.id; let i = $index) {
                <article
                  class="notification-item"
                  [class.notification-item--unread]="!notification.isRead"
                  [style.animation-delay]="(i * 50) + 'ms'"
                  (click)="handleNotification(notification)"
                >
                  <div class="notification-icon" [class]="'notification-icon--' + notification.type">
                    <lucide-icon [name]="getNotificationIcon(notification.type)"></lucide-icon>
                  </div>
                  <div class="notification-content">
                    <p class="notification-text">{{ notification.message }}</p>
                    <time class="notification-time">{{ formatTime(notification.createdAt) }}</time>
                  </div>
                  <button
                    mat-icon-button
                    class="dismiss-btn"
                    (click)="dismissNotification($event, notification)"
                    aria-label="Dismiss notification"
                  >
                    <lucide-icon name="x"></lucide-icon>
                  </button>
                  @if (!notification.isRead) {
                    <div class="unread-indicator"></div>
                  }
                </article>
              }
            </div>
          }
        } @else {
          <div class="notifications-empty">
            <lucide-icon name="bell"></lucide-icon>
            <p>No notifications yet</p>
          </div>
        }
      </div>

      @if (notifications().length > 0) {
        <div class="notifications-footer">
          <button mat-button routerLink="/activity" (click)="closeNotifications()">
            View All Activity
          </button>
        </div>
      }
    </div>
  </div>
}






